/**
 * Storage module for RPN Calculator
 * Handles localStorage with proper type safety
 *
 * Bug fixes applied:
 * - #3: Replaced eval() with JSON.parse() and explicit conversion
 */

import type {
  MemoryRegisters,
  StatisticsItem,
  ConversionConfig,
  DisplayMode,
  TrigMode,
  CommaType,
  StorageData
} from './types';

const STORAGE_VERSION = '2.0.0';

/**
 * Default values for all stored data
 */
const DEFAULTS: Required<StorageData> = {
  stack: [0, 0, 0, 0, 0],
  memoryregisters: {
    r0: 0, r1: 0, r2: 0, r3: 0, r4: 0,
    r5: 0, r6: 0, r7: 0, r8: 0, r9: 0
  },
  statisticsregisters: [],
  displaystack: false,
  displayprecision: 10,
  displaymode: 'normal',
  cmode: 'rad',
  audiofeedback: true,
  commatype: '3',
  conversionconfig: {
    fromfactor: 1,
    tofactor: 1,
    property: 'Acceleration',
    propertyindex: 0,
    fromvalue: 1,
    tovalue: 1
  },
  _version: STORAGE_VERSION
};

/**
 * Safe JSON parse with fallback
 * FIX #3: No eval() usage
 */
function safeJSONParse<T>(value: string | null, fallback: T): T {
  if (value === null) {
    return fallback;
  }
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

/**
 * Parse boolean from storage
 * FIX #3: Explicit string comparison instead of eval()
 */
function parseBoolean(value: string | null, fallback: boolean): boolean {
  if (value === null) {
    return fallback;
  }
  // FIX #3: Direct comparison, no eval()
  return value === 'true';
}

/**
 * Parse number from storage
 * FIX #3: Use parseInt/parseFloat instead of eval()
 */
function parseNumber(value: string | null, fallback: number): number {
  if (value === null) {
    return fallback;
  }
  // FIX #3: Direct parsing, no eval()
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? fallback : parsed;
}

/**
 * Validate display mode string
 * FIX #3: Explicit validation instead of eval()
 */
function parseDisplayMode(value: string | null, fallback: DisplayMode): DisplayMode {
  if (value === null) {
    return fallback;
  }
  // FIX #3: Whitelist validation instead of eval()
  const validModes: DisplayMode[] = ['normal', 'fix', 'sci', 'eng'];
  return validModes.includes(value as DisplayMode) ? (value as DisplayMode) : fallback;
}

/**
 * Validate trig mode string
 * FIX #3: Explicit validation instead of eval()
 */
function parseTrigMode(value: string | null, fallback: TrigMode): TrigMode {
  if (value === null) {
    return fallback;
  }
  // FIX #3: Whitelist validation instead of eval()
  const validModes: TrigMode[] = ['deg', 'rad', 'grad'];
  return validModes.includes(value as TrigMode) ? (value as TrigMode) : fallback;
}

/**
 * Validate comma type string
 */
function parseCommaType(value: string | null, fallback: CommaType): CommaType {
  if (value === null) {
    return fallback;
  }
  const validTypes: CommaType[] = ['1', '2', '3', '4'];
  return validTypes.includes(value as CommaType) ? (value as CommaType) : fallback;
}

/**
 * Storage class for managing calculator persistence
 */
export class Storage {
  /**
   * Load all calculator state from localStorage
   */
  static load(): StorageData {
    const data: StorageData = {};

    // Stack
    data.stack = safeJSONParse(
      localStorage.getItem('stack'),
      DEFAULTS.stack
    );

    // Memory registers
    data.memoryregisters = safeJSONParse<MemoryRegisters>(
      localStorage.getItem('memoryregisters'),
      DEFAULTS.memoryregisters
    );

    // Statistics registers
    data.statisticsregisters = safeJSONParse<StatisticsItem[]>(
      localStorage.getItem('statisticsregisters'),
      DEFAULTS.statisticsregisters
    );

    // Display stack preference
    data.displaystack = parseBoolean(
      localStorage.getItem('displaystack'),
      DEFAULTS.displaystack
    );

    // Display precision
    data.displayprecision = parseNumber(
      localStorage.getItem('displayprecision'),
      DEFAULTS.displayprecision
    );

    // Display mode - FIX #3: No eval()
    data.displaymode = parseDisplayMode(
      localStorage.getItem('displaymode'),
      DEFAULTS.displaymode
    );

    // Trig mode - FIX #3: No eval()
    data.cmode = parseTrigMode(
      localStorage.getItem('cmode'),
      DEFAULTS.cmode
    );

    // Audio feedback - FIX #3: No eval()
    data.audiofeedback = parseBoolean(
      localStorage.getItem('audiofeedback'),
      DEFAULTS.audiofeedback
    );

    // Comma type
    data.commatype = parseCommaType(
      localStorage.getItem('commatype'),
      DEFAULTS.commatype
    );

    // Conversion config
    data.conversionconfig = safeJSONParse<ConversionConfig>(
      localStorage.getItem('conversionconfig'),
      DEFAULTS.conversionconfig
    );

    // Version
    data._version = localStorage.getItem('_version') ?? undefined;

    return data;
  }

  /**
   * Save stack to localStorage
   */
  static saveStack(stack: number[]): void {
    localStorage.setItem('stack', JSON.stringify(stack));
  }

  /**
   * Save memory registers to localStorage
   */
  static saveMemoryRegisters(registers: MemoryRegisters): void {
    localStorage.setItem('memoryregisters', JSON.stringify(registers));
  }

  /**
   * Save statistics registers to localStorage
   */
  static saveStatisticsRegisters(registers: StatisticsItem[]): void {
    localStorage.setItem('statisticsregisters', JSON.stringify(registers));
  }

  /**
   * Save display stack preference
   */
  static saveDisplayStack(value: boolean): void {
    localStorage.setItem('displaystack', String(value));
  }

  /**
   * Save display precision
   */
  static saveDisplayPrecision(precision: number): void {
    localStorage.setItem('displayprecision', String(precision));
  }

  /**
   * Save display mode
   */
  static saveDisplayMode(mode: DisplayMode): void {
    localStorage.setItem('displaymode', mode);
  }

  /**
   * Save trig mode
   */
  static saveTrigMode(mode: TrigMode): void {
    localStorage.setItem('cmode', mode);
  }

  /**
   * Save audio feedback preference
   */
  static saveAudioFeedback(enabled: boolean): void {
    localStorage.setItem('audiofeedback', String(enabled));
  }

  /**
   * Save comma type preference
   */
  static saveCommaType(type: CommaType): void {
    localStorage.setItem('commatype', type);
  }

  /**
   * Save conversion config
   */
  static saveConversionConfig(config: ConversionConfig): void {
    localStorage.setItem('conversionconfig', JSON.stringify(config));
  }

  /**
   * Migrate old data format if needed
   */
  static migrate(): boolean {
    const currentVersion = localStorage.getItem('_version');

    if (currentVersion === STORAGE_VERSION) {
      return false; // No migration needed
    }

    // If no version, this is either fresh install or pre-2.0 data
    if (!currentVersion) {
      // Try to load existing data in old format
      const existingStack = localStorage.getItem('stack');

      if (existingStack) {
        // Data exists, just add version marker
        console.log('Migrating existing calculator data to v2.0.0');
      }
    }

    // Mark as migrated
    localStorage.setItem('_version', STORAGE_VERSION);
    return true;
  }

  /**
   * Clear all calculator data
   */
  static clear(): void {
    const keys = [
      'stack', 'memoryregisters', 'statisticsregisters',
      'displaystack', 'displayprecision', 'displaymode',
      'cmode', 'audiofeedback', 'commatype', 'conversionconfig',
      '_version'
    ];
    keys.forEach(key => localStorage.removeItem(key));
  }

  /**
   * Export all data as JSON string
   */
  static export(): string {
    return JSON.stringify(this.load(), null, 2);
  }

  /**
   * Import data from JSON string
   */
  static import(jsonString: string): boolean {
    try {
      const data = JSON.parse(jsonString) as StorageData;

      if (data.stack) this.saveStack(data.stack);
      if (data.memoryregisters) this.saveMemoryRegisters(data.memoryregisters);
      if (data.statisticsregisters) this.saveStatisticsRegisters(data.statisticsregisters);
      if (data.displaystack !== undefined) this.saveDisplayStack(data.displaystack);
      if (data.displayprecision !== undefined) this.saveDisplayPrecision(data.displayprecision);
      if (data.displaymode) this.saveDisplayMode(data.displaymode);
      if (data.cmode) this.saveTrigMode(data.cmode);
      if (data.audiofeedback !== undefined) this.saveAudioFeedback(data.audiofeedback);
      if (data.commatype) this.saveCommaType(data.commatype);
      if (data.conversionconfig) this.saveConversionConfig(data.conversionconfig);

      return true;
    } catch {
      return false;
    }
  }
}
